import {
  CaretRightIcon,
  CalendarDotsIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import type { EventSeriesDrawerProps } from '@/lib/types/events.types';
import { EventDetailsContent } from '@/content/EventDetailsContent';
import { formatEventDate } from '@/lib/utils/events.utils';
import type { Event } from '@/lib/types/database';

function isEventPast(event: Event): boolean {
  return new Date(event.start_time) < new Date();
}

function SeriesEventRow({
  event,
  isCurrent,
  onClick,
}: {
  event: Event;
  isCurrent: boolean;
  onClick: () => void;
}) {
  const isPast = isEventPast(event);
  const dateText = formatEventDate(event.start_time);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between py-5 text-left hover:bg-slate-600 transition-colors"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <CalendarDotsIcon
          size={24}
          weight="regular"
          className="text-purple-300 flex-shrink-0"
        />
        <span
          className={`text-large flex-1 ${
            isPast ? 'line-through text-slate-400' : 'text-slate-50'
          }`}
          style={{ fontSize: 'clamp(14px, 4vw, 18px)' }}
        >
          {event.title}
        </span>
      </div>
      <div className="flex items-center gap-2 text-slate-400 flex-shrink-0">
        <span className="text-[clamp(12px,3vw,14px)]">{dateText}</span>
        {isCurrent ? (
          <CheckIcon size={18} className="text-slate-50" />
        ) : (
          <CaretRightIcon size={18} weight="bold" />
        )}
      </div>
    </button>
  );
}

export function EventSeriesDrawer({
  open,
  onOpenChange,
  events,
  currentEventId,
  onEventClick,
}: EventSeriesDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
      <DrawerContent data-testid="event-series-drawer">
        <div className="mx-auto w-full">
          <DrawerHeader>
            <DrawerTitle>{EventDetailsContent.seriesDrawer.title}</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {events.map((event) => (
              <SeriesEventRow
                key={event.id}
                event={event}
                isCurrent={event.id === currentEventId}
                onClick={() => {
                  onEventClick(event.id);
                  onOpenChange(false);
                }}
              />
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

